import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonEmails.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonEmails.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import { EmailViewModel, CreatePersonEmailCommand, UpdatePersonEmailCommand } from '../../models/Entities/Person/Email/EmailModels';

export type KnownAction =
    | Actions.PersonEmails.PersonEmailGetAllRequestAction
    | Actions.PersonEmails.PersonEmailGetAllSuccessAction
    | Actions.PersonEmails.PersonEmailGetAllFailureAction
    | Actions.PersonEmails.PersonEmailGetByIdRequestAction
    | Actions.PersonEmails.PersonEmailGetByIdSuccessAction
    | Actions.PersonEmails.PersonEmailGetByIdFailureAction
    | Actions.PersonEmails.PersonEmailCreateRequestAction
    | Actions.PersonEmails.PersonEmailCreateSuccessAction
    | Actions.PersonEmails.PersonEmailCreateFailureAction
    | Actions.PersonEmails.PersonEmailUpdateRequestAction
    | Actions.PersonEmails.PersonEmailUpdateSuccessAction
    | Actions.PersonEmails.PersonEmailUpdateFailureAction
    | Actions.PersonEmails.PersonEmailDeleteRequestAction
    | Actions.PersonEmails.PersonEmailDeleteSuccessAction
    | Actions.PersonEmails.PersonEmailDeleteFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

export const actionCreators = {
    GetEmailsByPersonId: (personId: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EMAIL_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EMAIL_GETALL_REQUEST, personId });

        Services.GetEmailsByPersonId(token, personId)
            .then((response: EmailViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_EMAIL_GETALL_SUCCESS,
                    personId,
                    payload: response.map((item: EmailViewModel) => ({ ...item, personId }))
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_GETALL_FAILURE, error });
            });
    },

    GetEmailById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EMAIL_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EMAIL_GETBYID_REQUEST, id });

        Services.GetEmailById(token, id)
            .then((response: EmailViewModel) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_GETBYID_FAILURE, error });
            });
    },

    CreateEmail: (payload: CreatePersonEmailCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EMAIL_CREATE_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EMAIL_CREATE_REQUEST, payload });

        Services.CreateEmail(token, payload)
            .then((created: EmailViewModel) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_CREATE_SUCCESS, payload: { ...created, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_CREATE_FAILURE, error });
            });
    },

    UpdateEmail: (payload: UpdatePersonEmailCommand): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EMAIL_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EMAIL_UPDATE_REQUEST, payload });

        Services.UpdateEmail(token, payload)
            .then((updated: EmailViewModel) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_UPDATE_SUCCESS, payload: { ...updated, personId: payload.personId } });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_UPDATE_FAILURE, id: payload.id, error });
            });
    },

    DeleteEmail: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_EMAIL_DELETE_FAILURE, id, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_EMAIL_DELETE_REQUEST, id });

        Services.DeleteEmail(token, id)
            .then(() => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_DELETE_SUCCESS, id });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_EMAIL_DELETE_FAILURE, id, error });
            });
    }
};
